import type { CentreData } from './types';
import { naplanTestPackLabels, naplanTestPackGradesByCourse } from '@/config/naplanTestPacks';

export const tarneitData: CentreData = {
  slug: 'tarneit',
  name: 'Schoolola Tarneit',
  suburb: 'Tarneit',
  region: 'Western Melbourne',
  state: 'VIC',
  stateName: 'Victoria',
  status: 'operational',
  yearLevels: 'Prep - Year 8',
  canonicalPath: '/centres/tarneit/',
  heroImage: 'https://schoolola.com.au/centres/tarneit-og.jpg',
  address: {
    street: 'Dianella Community Centre, 6 Congo Dr',
    city: 'Tarneit',
    postcode: '3029',
    lat: -37.8292,
    lng: 144.6942,
  },
  contact: {
    phone: '03 7050 9606',
    phoneHref: '+61370509606',
    email: 'tarneit@schoololatutoring.com.au',
  },
  hours: {
    monday: { opens: '16:00', closes: '20:00' },
    tuesday: { opens: '16:00', closes: '20:00' },
    wednesday: { opens: '16:00', closes: '20:00' },
    thursday: { opens: '16:00', closes: '20:00' },
    friday: { opens: '16:00', closes: '20:00' },
    saturday: null,
    sunday: null,
  },
  heroSubtitle:
    'NAPLAN, selective entry and scholarship preparation by qualified Victorian Curriculum teachers.',
  trustStrip: [
    { k: 'In-person', v: 'Dianella Community Centre' },
    { k: 'Prep - Year 8', v: 'All year levels supported' },
    { k: 'Victorian Curriculum', v: 'Qualified teachers' },
  ],
  breadcrumb: { stateHref: '/states/victoria', regionLabel: 'Western Melbourne' },
  quickBook: {
    featuredCourses: [
      'Foundations Course',
      ...naplanTestPackLabels,
      'Scholarship Exam Preparation',
    ],
    eligibleGrades: ['Prep', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6', 'Year 7', 'Year 8'],
    gradesByCourse: {
      ...naplanTestPackGradesByCourse,
      'Foundations Course': ['Prep', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6', 'Year 7', 'Year 8'],
      'Scholarship Exam Preparation': ['Year 5', 'Year 6', 'Year 7', 'Year 8'],
    },
    defaultMeetingType: 'in-person',
    allowOnline: false,
    eyebrow: 'Quick Book',
    title: 'Book your free assessment in 60 seconds',
    subtitle: 'A few quick taps and we\u2019ll lock in a real time with our Tarneit team.',
  },
  pathwayIntro:
    'Tarneit sits in the catchment of Suzanne Cory High in Werribee - Victoria\u2019s closest fully selective school. Best results come from starting preparation in Year 5 and intensifying in Year 6 ahead of the Year 8 SEAL test.',
  googleRating: {
    value: 5.0,
    count: 3,
    profileUrl: 'https://www.google.com/search?q=Schoolola+Tarneit',
  },
  reviews: [
    {
      author: 'Parent from Tarneit',
      rating: 5,
      timeAgo: 'recently',
      datePublished: '2026-03-01',
      body: 'The tutors at Schoolola Tarneit are wonderful. My daughter has grown in confidence and her grades have improved significantly since starting here.',
    },
    {
      author: 'Local Family',
      rating: 5,
      timeAgo: 'recently',
      datePublished: '2026-02-15',
      body: 'Great environment for learning. The teachers are patient and really understand how to bring out the best in each child.',
    },
    {
      author: 'Tarneit Parent',
      rating: 5,
      timeAgo: 'recently',
      datePublished: '2026-02-01',
      body: 'We\u2019ve been really impressed with the structured approach and the progress our son has made in both maths and English.',
    },
  ],
  suburbsServed: ['Tarneit'],
  localSchools: [
    { name: 'Tarneit P-9 College', yearLevels: 'P-9', type: 'Public', distanceKm: 1.2 },
    { name: 'Tarneit Senior College', yearLevels: '10-12', type: 'Public', distanceKm: 1.8 },
    { name: 'Good News Lutheran College', yearLevels: 'P-12', type: 'Independent', distanceKm: 2.6 },
    { name: 'Thomas Carr College', yearLevels: '7-12', type: 'Catholic', distanceKm: 3.4 },
  ],
  selectiveSchools: [
    { name: 'Suzanne Cory High', location: 'Werribee', distanceKm: 9, status: { label: 'Closest selective', tone: 'info' } },
    { name: 'Mac.Robertson Girls', location: 'Melbourne', distanceKm: 22, status: { label: 'Very competitive', tone: 'warning' } },
    { name: 'Melbourne High', location: 'South Yarra', distanceKm: 26, notes: 'Boys', status: { label: 'Very competitive', tone: 'warning' } },
    { name: 'Nossal High', location: 'Berwick', distanceKm: 58, status: { label: 'Moderate demand', tone: 'info' } },
  ],
  programs: [
    {
      id: 'foundations',
      title: 'Foundations Course',
      yearPill: { label: 'Prep - Year 8', tone: 'success' },
      tagline: 'Core English and maths · all year levels',
      bullets: ['Phonics, fluency, comprehension', 'Number sense to algebra readiness', 'Writing structure across genres'],
      educationalLevel: 'Prep to Year 8',
      description: 'Core English and mathematics tutoring for Prep through Year 8 students at Schoolola Tarneit, aligned to the Victorian Curriculum.',
    },
    {
      id: 'naplan',
      title: 'NAPLAN Preparation',
      yearPill: { label: 'Yr 3, 5', tone: 'info' },
      tagline: '10-week term aligned to ACARA',
      bullets: ['Reading and writing', 'Numeracy strategies', 'Language conventions'],
      educationalLevel: 'Year 3 and Year 5',
      description: '10-week term NAPLAN preparation course covering reading, writing, language conventions and numeracy, aligned to ACARA test specifications.',
    },
    {
      id: 'sec',
      title: 'SEC Scholarship Exam Course',
      yearPill: { label: 'Yr 4-6', tone: 'info' },
      tagline: 'ACER and Edutest scholarship preparation',
      bullets: ['SEAL/SEALP training', 'Strategic exam techniques', 'Practice test cycles'],
      featured: 'popular',
      educationalLevel: 'Year 4 to Year 6',
      description: 'Schoolola SEC programmes - SEC TMRW (booklet-based) and the SEC Scholarship Exam Course - preparing students primarily for ACER and Edutest scholarship exams, with SEAL/SEALP training and structured practice test cycles.',
      prerequisites: 'Basic literacy and numeracy at year-level expectations.',
    },
    {
      id: 'assessment',
      title: 'General Assessment',
      yearPill: { label: 'All ages', tone: 'info' },
      tagline: 'Diagnostic assessment for new students',
      bullets: ['Reading, writing, maths diagnostic', '1:1 parent consultation', 'Personalised learning plan'],
      educationalLevel: 'All year levels',
      description: 'Free diagnostic assessment covering reading, writing and maths, followed by a 1:1 parent consultation and a written personalised learning plan.',
    },
  ],
  differentiators: [
    {
      title: 'Victorian Curriculum specialists',
      body: "Our team has analysed the curriculum in depth. Lessons map to what your child's school actually teaches, not generic worksheets.",
      icon: 'BookOpen',
    },
    {
      title: 'Measurable progress reports',
      body: 'Quarterly assessments tracked against ACARA bands. Parents see exactly where their child sits.',
      icon: 'LineChart',
    },
    {
      title: 'Small group ratios',
      body: 'Small group sizes for genuine individual attention while keeping the energy of peer learning.',
      icon: 'Users',
    },
    {
      title: 'Selective and scholarship track record',
      body: 'Network students placed at Suzanne Cory, Mac.Robertson Girls, Melbourne High and Nossal High.',
      icon: 'Trophy',
    },
  ],
  faqs: [
    {
      question: 'What year levels do you tutor in Tarneit?',
      answer: 'Prep through Year 8. Foundations covers all year levels in English and maths; NAPLAN, selective entry and scholarship programs target specific testing years.',
    },
    {
      question: 'Where is the Tarneit centre located?',
      answer: 'Schoolola Tarneit operates from the Dianella Community Centre at 6 Congo Drive, Tarneit VIC 3029.',
    },
    {
      question: 'What are your operating hours in Tarneit?',
      answer: 'Monday to Friday from 4:00 PM to 8:00 PM. Assessment bookings are available in 30-minute slots, with the last slot at 7:30 PM.',
    },
    {
      question: 'Do you offer online tutoring at Tarneit?',
      answer: 'The Tarneit centre is in-person only. Families wanting online tutoring can book through one of our other Victorian centres.',
    },
    {
      question: 'What does the free assessment include?',
      answer: 'A full diagnostic assessment, a 1:1 parent consultation, and a written learning plan with the recommended programme.',
    },
    {
      question: 'Which selective schools do Tarneit students target?',
      answer: 'Suzanne Cory High in Werribee is the closest (around 9km). Our Selective Entry program also covers Mac.Robertson Girls, Melbourne High and Nossal High via the Year 8 SEAL test pathway.',
    },
  ],
};
